import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/auth";
import PermissionGuard from "./PermissionGuard";
import ConfirmDialog from "./ConfirmDialog";

const User = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userEmail: '', hasBookmarks: false, bookmarkCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError("");

        if (!isAuthenticated || !token) {
          setError("Please login to view users");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${baseUrl}/auth/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: source.token,
        });

        if (isMounted) {
          setUsers(response.data);
        }
      } catch (error) {
        if (isMounted && !axios.isCancel(error)) {
          console.error("Error fetching users:", error);
          if (error.response?.status === 401) {
            setError("Authentication failed. Please login again.");
          } else {
            setError("Failed to load users. Please try again.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      isMounted = false;
      source.cancel("Component unmounted. Request canceled.");
    };
  }, [baseUrl, token, isAuthenticated]);

  const handleAdd = () => navigate("/dashboard/register");
  const handleEdit = (id) => navigate(`/dashboard/editUsers/${id}`);

  const handleOpenDialog = async (id) => {
    if (!isAuthenticated || !token) {
      setError("Please login to delete users");
      return;
    }

    const user = users.find(u => u.id === id);
    if (!user) return;

    try {
      // Check if user has linked bookmarks
      const bookmarksResponse = await axios.get(`${baseUrl}/auth/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userBookmarks = bookmarksResponse.data.filter(b => b.userId === id);

      setDeleteDialog({
        open: true,
        userId: id,
        userEmail: user.email,
        hasBookmarks: userBookmarks.length > 0,
        bookmarkCount: userBookmarks.length
      });
    } catch (error) {
      console.error("Error checking user bookmarks:", error);
      setDeleteDialog({
        open: true,
        userId: id,
        userEmail: user.email,
        hasBookmarks: false,
        bookmarkCount: 0
      });
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialog({ open: false, userId: null, userEmail: '', hasBookmarks: false, bookmarkCount: 0 });
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${baseUrl}/auth/users/${deleteDialog.userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(users.filter((user) => user.id !== deleteDialog.userId));
      handleCloseDialog();
    } catch (error) {
      console.error("Error deleting user:", error);
      setError(error.response?.data?.error || "Failed to delete user. Please try again.");
      setDeleteLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.firstname?.toLowerCase().includes(searchLower) ||
      user.lastname?.toLowerCase().includes(searchLower) ||
      user.username?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });

  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          User Management
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Please login to view users.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          User Management
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          User Management
        </Typography>
        <PermissionGuard resource="user" action="create">
          <Button variant="contained" onClick={handleAdd} >
            Register User
          </Button>
        </PermissionGuard>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Search users..."
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .MuiInputLabel-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      />

      {filteredUsers.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {users.length === 0
            ? "No users found."
            : "No users match your search criteria."}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>First Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Last Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Username</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Role</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.firstname}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.lastname}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.username}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.email}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.role}</TableCell>
                  <TableCell>
                    <PermissionGuard resource="user" action="edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(user.id)}
                        title="Edit User"
                        
                      >
                        <EditIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                    <PermissionGuard resource="user" action="delete">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDialog(user.id)}
                        title="Delete User"
                        
                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Are you sure you want to delete user "${deleteDialog.userEmail}"?`}
        warning={
          deleteDialog.hasBookmarks
            ? `⚠️ This user has ${deleteDialog.bookmarkCount} linked bookmark(s). Deleting this user will also delete these bookmarks.`
            : null
        }
        severity={deleteDialog.hasBookmarks ? 'error' : 'warning'}
        loading={deleteLoading}
        confirmText="Delete User"
        confirmColor="error"
      />
    </Container>
  );
};

export default User;
